import React from 'react';
import { DEPARTMENT_COLORS } from '../types/department';

function DepartmentCard({
  department,
  onViewAll,
  darkMode: d,
}) {
  const colors = DEPARTMENT_COLORS[department.accentColor];

  // Determine activity level based on total entries
  const getActivityLevel = (total) => {
    if (total === 0) return 'Low';
    if (total >= 100) return 'High';
    if (total >= 50) return 'Active';
    return 'Growing';
  };

  const activityLevel = getActivityLevel(department.totalEntries);
  const activityColors = {
    High: { bg: '#fef2f2', text: '#dc2626' },
    Active: { bg: '#eff6ff', text: '#2563eb' },
    Growing: { bg: '#f0fdf4', text: '#16a34a' },
    Low: { bg: '#fef3c7', text: '#d97706' },
  };

  return (
    <div
      style={{
        background: d ? 'rgba(255,255,255,0.04)' : '#fff',
        border: `1px solid ${d ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
        borderRadius: '12px',
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        boxShadow: d ? '0 2px 8px rgba(0,0,0,0.2)' : '0 1px 3px rgba(15,37,87,0.06)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = colors[600];
        e.currentTarget.style.boxShadow = d
          ? `0 8px 20px rgba(0,0,0,0.3)`
          : `0 8px 20px ${colors[600]}20`;
        e.currentTarget.style.transform = 'translateY(-4px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = d ? 'rgba(255,255,255,0.08)' : '#e2e8f0';
        e.currentTarget.style.boxShadow = d
          ? '0 2px 8px rgba(0,0,0,0.2)'
          : '0 1px 3px rgba(15,37,87,0.06)';
        e.currentTarget.style.transform = 'none';
      }}
    >
      {/* Header with department name and activity badge */}
      <div
        style={{
          background: colors[50],
          borderBottom: `0.5px solid ${colors[200]}`,
          padding: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <div style={{ flex: 1 }}>
          <h3
            style={{
              fontSize: '16px',
              fontWeight: '800',
              color: colors[800],
              margin: '0 0 4px 0',
            }}
          >
            {department.name}
          </h3>
          <p
            style={{
              fontSize: '13px',
              color: colors[600],
              margin: '0',
              fontWeight: '600',
            }}
          >
            {department.facultyCount} Faculty Members
          </p>
        </div>
        <div
          style={{
            background: activityColors[activityLevel].bg,
            color: activityColors[activityLevel].text,
            padding: '4px 12px',
            borderRadius: '16px',
            fontSize: '12px',
            fontWeight: '700',
            whiteSpace: 'nowrap',
            marginLeft: '8px',
          }}
        >
          {activityLevel} Activity
        </div>
      </div>

      {/* Stats chips */}
      <div style={{ padding: '16px', borderBottom: `1px solid ${d ? 'rgba(255,255,255,0.06)' : '#f1f5f9'}` }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '8px',
          }}
        >
          <div
            style={{
              background: d ? 'rgba(255,255,255,0.04)' : '#f8fafc',
              border: `1px solid ${d ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
              borderRadius: '8px',
              padding: '10px',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                fontSize: '20px',
                fontWeight: '900',
                color: colors[600],
                lineHeight: '1',
              }}
            >
              {department.stats.articlePublications}
            </div>
            <div
              style={{
                fontSize: '11px',
                color: d ? '#94a3b8' : '#64748b',
                fontWeight: '600',
                marginTop: '2px',
              }}
            >
              Articles
            </div>
          </div>
          <div
            style={{
              background: d ? 'rgba(255,255,255,0.04)' : '#f8fafc',
              border: `1px solid ${d ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
              borderRadius: '8px',
              padding: '10px',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                fontSize: '20px',
                fontWeight: '900',
                color: colors[600],
                lineHeight: '1',
              }}
            >
              {department.stats.textBookPublications}
            </div>
            <div
              style={{
                fontSize: '11px',
                color: d ? '#94a3b8' : '#64748b',
                fontWeight: '600',
                marginTop: '2px',
              }}
            >
              Textbooks
            </div>
          </div>
          <div
            style={{
              background: d ? 'rgba(255,255,255,0.04)' : '#f8fafc',
              border: `1px solid ${d ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
              borderRadius: '8px',
              padding: '10px',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                fontSize: '20px',
                fontWeight: '900',
                color: colors[600],
                lineHeight: '1',
              }}
            >
              {department.stats.patents}
            </div>
            <div
              style={{
                fontSize: '11px',
                color: d ? '#94a3b8' : '#64748b',
                fontWeight: '600',
                marginTop: '2px',
              }}
            >
              Patents
            </div>
          </div>
          <div
            style={{
              background: d ? 'rgba(255,255,255,0.04)' : '#f8fafc',
              border: `1px solid ${d ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
              borderRadius: '8px',
              padding: '10px',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                fontSize: '20px',
                fontWeight: '900',
                color: colors[600],
                lineHeight: '1',
              }}
            >
              {department.stats.copyRights}
            </div>
            <div
              style={{
                fontSize: '11px',
                color: d ? '#94a3b8' : '#64748b',
                fontWeight: '600',
                marginTop: '2px',
              }}
            >
              Copyrights
            </div>
          </div>
        </div>
      </div>

      {/* View All Button */}
      <div style={{ padding: '12px 16px' }}>
        <button
          onClick={() => onViewAll(department)}
          style={{
            width: '100%',
            background: d ? 'rgba(255,255,255,0.06)' : colors[50],
            border: `1.5px solid ${colors[200]}`,
            borderRadius: '8px',
            padding: '10px',
            color: colors[600],
            fontWeight: '700',
            fontSize: '14px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = d ? 'rgba(255,255,255,0.1)' : colors[100];
            e.currentTarget.style.borderColor = colors[600];
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = d ? 'rgba(255,255,255,0.06)' : colors[50];
            e.currentTarget.style.borderColor = colors[200];
          }}
        >
          View all faculty
          <span style={{ fontSize: '16px', lineHeight: '1' }}>→</span>
        </button>
      </div>
    </div>
  );
}

export default DepartmentCard;
